import { useMemo, useState, useEffect, useCallback } from "react";
import { ArrowLeft, Search, X, Edit2, Trash2, Plus, ChevronRight, FileSpreadsheet, Building2, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { stages, compactMoney, badgeTone, errorMessage } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";
import { EditOrderModal, DeleteOrderConfirmationModal, NewOrderModal } from "./Orders";
import BulkOrderUploadModal from "./BulkOrderUploadModal";

export default function ClientOrders({
  clientId,
  orders = [],
  clients = [],
  user,
  onBack,
  onOpenOrder,
  onChange,
}) {
  const [search, setSearch] = useSavedState(`client_orders_${clientId}`, "search", "");
  const [stage, setStage] = useSavedState(`client_orders_${clientId}`, "stage", "");
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [busyOrderId, setBusyOrderId] = useState("");
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkConfirmModal, setBulkConfirmModal] = useState(null); // { targetStage: "Design" | "Production", count: number, orderIds?: string[], initialUser?: string }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [designUsers, setDesignUsers] = useState([]);
  const [productionUsers, setProductionUsers] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [reassignModalOrder, setReassignModalOrder] = useState(null);
  const [reassignUser, setReassignUser] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);

  const canManage = isAdmin(user) || canAccessDepartment(user, ["Sales"]);

  const loadEligibleUsers = useCallback(async () => {
    try {
      const [dRes, pRes] = await Promise.all([
        api("get", "/users?department=Design&status=active"),
        api("get", "/users?department=Production&status=active"),
      ]);
      setDesignUsers(Array.isArray(dRes?.data) ? dRes.data : []);
      setProductionUsers(Array.isArray(pRes?.data) ? pRes.data : []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadEligibleUsers();
  }, [loadEligibleUsers]);

  const handleOpenSendModal = (order, targetDept) => {
    const currentAssigned = targetDept === "Design" ? (order.assigned_designer || "") : (order.assigned_producer || "");
    setBulkAssignee(currentAssigned);
    setBulkConfirmModal({
      targetStage: targetDept,
      count: 1,
      orderIds: [order.order_id],
      initialUser: currentAssigned,
    });
  };

  const handleSendToDepartment = async (order, targetDept) => {
    handleOpenSendModal(order, targetDept);
  };

  const handleExecuteBulkMove = async (assignedUserToSet) => {
    if (!bulkConfirmModal) return;
    const targetOrderIds = bulkConfirmModal.orderIds || selectedOrderIds;
    if (!targetOrderIds || targetOrderIds.length === 0) return;

    const assignedVal = assignedUserToSet !== undefined ? assignedUserToSet : bulkAssignee;

    setBulkBusy(true);
    try {
      const res = await api("post", "/orders/bulk-stage", {
        order_ids: targetOrderIds,
        target_stage: bulkConfirmModal.targetStage,
        assigned_user: assignedVal !== undefined ? assignedVal : undefined,
      });
      const updatedCount = res.data?.updated_count || targetOrderIds.length;
      const assignedMsg = assignedVal ? ` and assigned to ${assignedVal}` : "";
      toast.success(`Moved ${updatedCount} order${updatedCount === 1 ? "" : "s"} to ${bulkConfirmModal.targetStage}${assignedMsg}`);
      setSelectedOrderIds([]);
      setBulkConfirmModal(null);
      setBulkAssignee("");
      onChange && onChange();
    } catch (err) {
      toast.error(errorMessage(err, `Failed to move selected orders to ${bulkConfirmModal.targetStage}`));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleOpenReassign = (order) => {
    const isProd = order.current_stage === "Production";
    const current = isProd ? (order.assigned_producer || "") : (order.assigned_designer || "");
    setReassignModalOrder(order);
    setReassignUser(current);
  };

  const handleSaveReassign = async () => {
    if (!reassignModalOrder) return;
    setReassignBusy(true);
    try {
      const isProd = reassignModalOrder.current_stage === "Production";
      const payload = isProd
        ? { assigned_producer: reassignUser }
        : { assigned_designer: reassignUser };

      await api("patch", `/orders/${reassignModalOrder.order_id}`, payload);
      toast.success(reassignUser ? `Order assigned to ${reassignUser}` : "Assignment removed");
      setReassignModalOrder(null);
      onChange && onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to update assignment"));
    } finally {
      setReassignBusy(false);
    }
  };

  // Resolve client info
  const clientInfo = useMemo(() => {
    const fromClients = clients.find(
      (c) => c.client_id === clientId || c.company_name?.toLowerCase() === clientId?.toLowerCase()
    );

    const clientOrders = orders.filter(
      (o) => o.client_id === clientId || o.client_name?.toLowerCase() === clientId?.toLowerCase()
    );

    const firstOrder = clientOrders[0];

    return {
      clientId: fromClients?.client_id || firstOrder?.client_id || clientId,
      companyName: fromClients?.company_name || firstOrder?.client_name || clientId || "Client",
      company: fromClients?.company || firstOrder?.company || "",
      contactPerson: fromClients?.contact_person || firstOrder?.client_contact_person || "—",
      email: fromClients?.email || firstOrder?.client_email || "—",
      phone: fromClients?.phone || firstOrder?.client_phone || "",
      allOrders: clientOrders.filter((o) => !o.deleted),
    };
  }, [clientId, clients, orders]);

  // Metrics for this client
  const clientMetrics = useMemo(() => {
    const list = clientInfo.allOrders;
    const total = list.length;
    const completed = list.filter((o) =>
      ["Delivered", "Completed", "Archived"].includes(o.current_stage)
    ).length;
    const active = total - completed;
    const totalValue = list.reduce((sum, o) => sum + (o.total_value || 0), 0);

    return { total, active, completed, totalValue };
  }, [clientInfo.allOrders]);

  // Filtered orders for this client
  const filteredOrders = useMemo(() => {
    return clientInfo.allOrders.filter((o) => {
      // Stage filter
      if (stage) {
        if (stage === "Dispatch") {
          if (!["Dispatch", "Challan", "Delivery"].includes(o.current_stage)) return false;
        } else if (stage === "Design") {
          if (!["Design", "Client Approval"].includes(o.current_stage)) return false;
        } else if (o.current_stage !== stage) {
          return false;
        }
      }

      // Search filter
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const match =
          (o.order_id || "").toLowerCase().includes(q) ||
          (o.description || "").toLowerCase().includes(q) ||
          (o.product || "").toLowerCase().includes(q) ||
          (o.product_code || "").toLowerCase().includes(q) ||
          (o.po_number || "").toLowerCase().includes(q) ||
          (o.client_contact_person || "").toLowerCase().includes(q);
        if (!match) return false;
      }

      return true;
    });
  }, [clientInfo.allOrders, stage, search]);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    paginatedItems: paginatedOrders,
    totalItems: totalFilteredOrders,
  } = usePagination(filteredOrders, 10, { sectionKey: `client_orders_${clientId}` });

  const isOrderEligible = (o) => (o.current_stage === "Order Received" || !o.current_stage);
  const eligiblePageOrders = paginatedOrders.filter(isOrderEligible);
  const isAllPageSelected = eligiblePageOrders.length > 0 && eligiblePageOrders.every((o) => selectedOrderIds.includes(o.order_id));
  const isSomePageSelected = eligiblePageOrders.some((o) => selectedOrderIds.includes(o.order_id));

  const toggleSelectAll = () => {
    if (isAllPageSelected) {
      const pageIds = new Set(eligiblePageOrders.map((o) => o.order_id));
      setSelectedOrderIds((prev) => prev.filter((id) => !pageIds.has(id)));
    } else {
      const newIds = new Set([...selectedOrderIds, ...eligiblePageOrders.map((o) => o.order_id)]);
      setSelectedOrderIds(Array.from(newIds));
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  return (
    <div className="content detail-content" data-testid="client-orders-page">
      {/* Back Button */}
      <button className="back-btn" data-testid="client-orders-back-button" onClick={onBack}>
        <ArrowLeft size={14} /> Back to Orders
      </button>

      {/* Detail Head */}
      <div className="detail-head" style={{ marginBottom: 20 }}>
        <div>
          <div className="small-label">OPERATIONS · CLIENT ORDERS</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "4px 0 6px" }}>
            <h1 style={{ margin: 0 }}>{clientInfo.companyName}</h1>
            {clientInfo.company && (
              <span
                style={{
                  fontSize: 12,
                  padding: "3px 8px",
                  background: "#e0f2fe",
                  color: "#0369a1",
                  borderRadius: 4,
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
                data-testid="client-orders-company-badge"
              >
                <Building2 size={12} /> {clientInfo.company}
              </span>
            )}
          </div>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            {[
              clientInfo.contactPerson !== "—" ? `Contact: ${clientInfo.contactPerson}` : null,
              clientInfo.email !== "—" ? clientInfo.email : null,
              canViewClientPhone(user) && clientInfo.phone ? `📞 ${clientInfo.phone}` : null,
            ]
              .filter(Boolean)
              .join(" · ") || "No contact info recorded"}
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {canManage && (
            <>
              <button
                className="outline-btn compact"
                onClick={() => setShowBulkUpload(true)}
                data-testid="client-orders-bulk-btn"
                style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
              >
                <FileSpreadsheet size={15} /> Upload Multiple Orders
              </button>
              <button
                className="primary-btn compact"
                onClick={() => setShowNewOrder(true)}
                data-testid="client-orders-new-btn"
              >
                <Plus size={15} /> New order
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="metrics-grid" style={{ marginBottom: 20 }}>
        <div className="metric blue">
          <div className="metric-top"><span>Total Orders</span></div>
          <strong>{clientMetrics.total}</strong>
          <small>All time orders</small>
        </div>
        <div className="metric amber">
          <div className="metric-top"><span>Active Orders</span></div>
          <strong>{clientMetrics.active}</strong>
          <small>In workflow</small>
        </div>
        <div className="metric green">
          <div className="metric-top"><span>Completed Orders</span></div>
          <strong>{clientMetrics.completed}</strong>
          <small>Delivered & finalized</small>
        </div>
        <div className="metric purple">
          <div className="metric-top"><span>Total Order Value</span></div>
          <strong>{compactMoney(clientMetrics.totalValue)}</strong>
          <small>Lifetime client commitment</small>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="toolbar" style={{ marginBottom: 16 }}>
        <div className="search-box">
          <Search size={16} />
          <input
            data-testid="client-orders-search-input"
            placeholder="Search order ID, product, PO…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          {search && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
              style={{ padding: 2, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <select
          data-testid="client-orders-stage-filter"
          value={stage}
          onChange={(e) => {
            setStage(e.target.value);
            setCurrentPage(1);
          }}
          style={{ minWidth: 150 }}
        >
          <option value="">All stages ({clientInfo.allOrders.length})</option>
          {stages.map((s) => {
            const count = clientInfo.allOrders.filter((o) => o.current_stage === s).length;
            return (
              <option key={s} value={s}>
                {s} ({count})
              </option>
            );
          })}
        </select>
      </div>

      {/* Orders Table Panel */}
      <section className="panel">
        <div className="panel-title">
          <div>
            <h3>
              Orders <span className="count">{filteredOrders.length}</span>
            </h3>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>
            Click any order to view details &amp; workflow
          </span>
        </div>

        {canManage && selectedOrderIds.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 14px",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 6,
              marginBottom: 10,
            }}
            data-testid="client-orders-bulk-actions-bar"
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#166534" }}>
              <span>{selectedOrderIds.length} order{selectedOrderIds.length === 1 ? "" : "s"} selected</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                className="primary-btn compact"
                onClick={() => {
                  setBulkAssignee("");
                  setBulkConfirmModal({ targetStage: "Design", count: selectedOrderIds.length, orderIds: selectedOrderIds, initialUser: "" });
                }}
                data-testid="client-orders-bulk-send-design"
                style={{ fontSize: 12, padding: "4px 12px", background: "#0284c7", borderColor: "#0284c7" }}
              >
                Send to Design
              </button>
              <button
                type="button"
                className="primary-btn compact"
                onClick={() => {
                  setBulkAssignee("");
                  setBulkConfirmModal({ targetStage: "Production", count: selectedOrderIds.length, orderIds: selectedOrderIds, initialUser: "" });
                }}
                data-testid="client-orders-bulk-send-production"
                style={{ fontSize: 12, padding: "4px 12px", background: "#16a34a", borderColor: "#16a34a" }}
              >
                Send to Production
              </button>
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => setSelectedOrderIds([])}
                data-testid="client-orders-bulk-clear-selection"
                style={{ fontSize: 12, padding: "4px 10px" }}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        <div className="table-wrap" style={{ overflowX: "auto" }}>
          <table style={{ minWidth: "1200px" }}>
            <thead>
              <tr>
                {canManage && (
                  <th style={{ width: 40, padding: "8px 6px 8px 14px" }}>
                    <input
                      type="checkbox"
                      checked={isAllPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !isAllPageSelected && isSomePageSelected;
                      }}
                      onChange={toggleSelectAll}
                      title="Select all eligible orders on this page"
                      data-testid="client-orders-select-all-checkbox"
                      style={{ cursor: "pointer" }}
                    />
                  </th>
                )}
                <th style={{ width: 130 }}>Order ID</th>
                <th style={{ minWidth: 160 }}>Description</th>
                <th style={{ width: 140 }}>Product</th>
                <th style={{ textAlign: "right", width: 90 }}>Quantity</th>
                <th style={{ width: 60 }}>Unit</th>
                <th style={{ textAlign: "right", width: 110 }}>Value/Price</th>
                <th style={{ width: 110 }}>Delivery Due</th>
                <th style={{ width: 130 }}>Workflow Stage</th>
                <th style={{ width: 145 }}>Assigned To</th>
                {canManage && <th style={{ textAlign: "right", width: 220, paddingRight: 16 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((o) => {
                const isEligible = o.current_stage === "Order Received" || !o.current_stage;
                const isSelected = selectedOrderIds.includes(o.order_id);
                const isDesignStage = o.current_stage === "Design" || Boolean(o.assigned_designer);
                const isProdStage = o.current_stage === "Production" || Boolean(o.assigned_producer);
                const assignedName = o.current_stage === "Design"
                  ? (o.assigned_designer || "")
                  : o.current_stage === "Production"
                  ? (o.assigned_producer || "")
                  : (o.assigned_designer || o.assigned_producer || "");

                return (
                  <tr
                    key={o.order_id}
                    onClick={() => onOpenOrder && onOpenOrder(o.order_id)}
                    data-testid={`client-order-row-${o.order_id}`}
                    style={{ cursor: "pointer", background: isSelected ? "#f8fafc" : undefined }}
                  >
                    {canManage && (
                      <td style={{ width: 40, padding: "8px 6px 8px 14px" }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!isEligible}
                          onChange={() => isEligible && toggleOrderSelection(o.order_id)}
                          title={isEligible ? "Select order for bulk action" : "This order has already progressed beyond Order Received"}
                          data-testid={`client-order-checkbox-${o.order_id}`}
                          style={{ cursor: isEligible ? "pointer" : "not-allowed" }}
                        />
                      </td>
                    )}
                    <td style={{ width: 130 }}>
                      <b className="mono" style={{ color: "#0284c7", fontSize: 12.5 }}>{o.order_id}</b>
                      <small style={{ display: "block", color: "#94a3b8", fontSize: 11, marginTop: 2 }}>{o.order_date || "—"}</small>
                    </td>
                    <td style={{ color: "#334155", fontSize: 12.5, lineHeight: 1.4, minWidth: 160 }}>
                      {o.description ? (
                        <span>{o.description}</span>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>—</span>
                      )}
                    </td>
                    <td style={{ width: 140 }}>
                      <b style={{ fontSize: 13, color: "#1e293b" }}>{o.product || "—"}</b>
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600, color: "var(--navy)", width: 90, fontSize: 13 }}>
                      {o.quantity !== undefined && o.quantity !== null && o.quantity !== ""
                        ? Number(o.quantity).toLocaleString()
                        : "—"}
                    </td>
                    <td style={{ color: "#64748b", width: 60, fontSize: 12 }}>
                      {o.unit || "pcs"}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap", width: 110, fontSize: 13, fontWeight: 600 }}>
                      {o.value !== undefined && o.value !== null && Number(o.value) > 0
                        ? `₹${Number(o.value).toLocaleString("en-IN")}`
                        : (o.order_value && Number(o.order_value) > 0
                          ? `₹${Number(o.order_value).toLocaleString("en-IN")}`
                          : "—")}
                    </td>
                    <td style={{ color: "#475569", fontSize: 12.5, whiteSpace: "nowrap", width: 110 }}>
                      {o.required_delivery_date || "—"}
                    </td>
                    <td style={{ width: 130 }}>
                      <span className={`badge ${badgeTone(o.current_stage)}`} style={{ fontSize: 11 }}>
                        {o.current_stage || "Order Received"}
                      </span>
                    </td>
                    <td style={{ width: 145 }} onClick={(e) => e.stopPropagation()}>
                      {assignedName ? (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontWeight: 600,
                              fontSize: 11.5,
                              padding: "2px 7px",
                              borderRadius: 4,
                              background: isDesignStage ? "#f0f9ff" : isProdStage ? "#f0fdf4" : "#f1f5f9",
                              color: isDesignStage ? "#0369a1" : isProdStage ? "#15803d" : "#334155",
                              border: `1px solid ${isDesignStage ? "#bae6fd" : isProdStage ? "#bbf7d0" : "#e2e8f0"}`,
                              maxWidth: 130,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={`Assigned to: ${assignedName}`}
                            data-testid={`client-order-assigned-${o.order_id}`}
                          >
                            <User size={12} style={{ flexShrink: 0 }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{assignedName}</span>
                          </span>
                          {canManage && (
                            <button
                              type="button"
                              className="icon-btn compact"
                              onClick={() => handleOpenReassign(o)}
                              title="Change or remove assignment"
                              data-testid={`btn-reassign-${o.order_id}`}
                              style={{ padding: 2, color: "#64748b", background: "none", border: "none", cursor: "pointer" }}
                            >
                              <Edit2 size={12} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <span style={{ color: "#94a3b8", fontSize: 12 }} data-testid={`client-order-assigned-${o.order_id}`}>
                            —
                          </span>
                          {canManage && (o.current_stage === "Design" || o.current_stage === "Production") && (
                            <button
                              type="button"
                              className="outline-btn compact"
                              onClick={() => handleOpenReassign(o)}
                              style={{ fontSize: 10, padding: "1px 6px", height: 20, color: "#64748b", borderRadius: 4 }}
                              data-testid={`btn-assign-${o.order_id}`}
                              title="Assign user"
                            >
                              + Assign
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    {canManage && (
                      <td style={{ textAlign: "right", width: 220, paddingRight: 16 }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center", flexWrap: "nowrap" }}>
                          {(o.current_stage === "Order Received" || !o.current_stage) && (
                            <>
                              <button
                                type="button"
                                className="outline-btn compact"
                                disabled={busyOrderId === o.order_id}
                                onClick={() => handleOpenSendModal(o, "Design")}
                                data-testid={`order-send-design-${o.order_id}`}
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  padding: "3px 9px",
                                  height: 26,
                                  borderColor: "#0284c7",
                                  color: "#0284c7",
                                  background: "#f0f9ff",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                                title="Send to Design (with optional assignment)"
                              >
                                Design
                              </button>
                              <button
                                type="button"
                                className="outline-btn compact"
                                disabled={busyOrderId === o.order_id}
                                onClick={() => handleOpenSendModal(o, "Production")}
                                data-testid={`order-send-production-${o.order_id}`}
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  padding: "3px 9px",
                                  height: 26,
                                  borderColor: "#16a34a",
                                  color: "#16a34a",
                                  background: "#f0fdf4",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                                title="Send directly to Production (with optional assignment)"
                              >
                                Production
                              </button>
                            </>
                          )}
                          <button
                            className="icon-btn"
                            onClick={() => setEditingOrder(o)}
                            title="Edit order"
                            data-testid={`client-order-edit-${o.order_id}`}
                            style={{ padding: 5 }}
                          >
                            <Edit2 size={14} />
                          </button>
                          {isAdmin(user) && (
                            <button
                              className="icon-btn"
                              onClick={() => setDeletingOrder(o)}
                              title="Delete order"
                              style={{ color: "var(--red)", padding: 5 }}
                              data-testid={`client-order-delete-${o.order_id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredOrders.length === 0 && (
            <div className="empty-inline" data-testid="client-orders-empty">
              {search || stage
                ? "No orders match your search or filter criteria for this client."
                : "No orders found for this client."}
            </div>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalFilteredOrders}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          itemLabel="orders"
        />
      </section>

      {/* Modals */}
      {showNewOrder && (
        <NewOrderModal
          clients={clients}
          initialClientId={clientInfo.clientId}
          onClose={() => setShowNewOrder(false)}
          onCreated={() => {
            onChange && onChange();
          }}
        />
      )}

      {showBulkUpload && (
        <BulkOrderUploadModal
          client={{ client_id: clientInfo.clientId, company_name: clientInfo.companyName }}
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          onCreated={() => {
            onChange && onChange();
          }}
        />
      )}

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          clients={clients}
          onClose={() => setEditingOrder(null)}
          onUpdated={() => {
            onChange && onChange();
          }}
        />
      )}

      {deletingOrder && (
        <DeleteOrderConfirmationModal
          order={deletingOrder}
          onClose={() => setDeletingOrder(null)}
          onDeleted={() => {
            onChange && onChange();
          }}
        />
      )}

      {/* Bulk Move Confirmation Modal */}
      {bulkConfirmModal && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10001 }}
          data-testid="client-orders-bulk-confirmation-backdrop"
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 440, padding: 24 }}
            data-testid="client-orders-bulk-confirmation-modal"
          >
            <div className="modal-head" style={{ marginBottom: 12 }}>
              <div>
                <div className="small-label">CONFIRM BULK ACTION</div>
                <h2 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>
                  Move {bulkConfirmModal.count} selected order{bulkConfirmModal.count === 1 ? "" : "s"} to {bulkConfirmModal.targetStage}?
                </h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => !bulkBusy && setBulkConfirmModal(null)}
                disabled={bulkBusy}
                data-testid="bulk-confirm-close-btn"
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px 0", lineHeight: 1.5 }}>
              {bulkConfirmModal.targetStage === "Design"
                ? `The selected ${bulkConfirmModal.count} order(s) will be transitioned to Design (New) stage.`
                : `The selected ${bulkConfirmModal.count} order(s) will be transitioned directly to Production stage.`}
            </p>
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--navy)",
                  marginBottom: 6,
                }}
              >
                Assign to {bulkConfirmModal.targetStage === "Design" ? "Design" : "Production"} Team Member (Optional):
              </label>
              <select
                value={bulkAssignee}
                onChange={(e) => setBulkAssignee(e.target.value)}
                disabled={bulkBusy}
                data-testid="bulk-assign-user-select"
                style={{
                  width: "100%",
                  height: 38,
                  fontSize: 13,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid var(--line)",
                  background: "#fff",
                  color: "var(--navy)",
                }}
              >
                <option value="">-- Leave Unassigned --</option>
                {(bulkConfirmModal.targetStage === "Design" ? designUsers : productionUsers).map((u) => (
                  <option key={u.user_id || u.id || u.email} value={u.name}>
                    {u.name} {u.role ? `(${u.role})` : ""}
                  </option>
                ))}
              </select>
              <small style={{ display: "block", fontSize: 11, color: "#64748b", marginTop: 4 }}>
                Only active users from the {bulkConfirmModal.targetStage} department are listed.
              </small>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setBulkConfirmModal(null)}
                disabled={bulkBusy}
                data-testid="bulk-confirm-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => handleExecuteBulkMove()}
                disabled={bulkBusy}
                data-testid="bulk-confirm-proceed-btn"
                style={
                  bulkConfirmModal.targetStage === "Design"
                    ? { background: "#0284c7", borderColor: "#0284c7" }
                    : { background: "#16a34a", borderColor: "#16a34a" }
                }
              >
                {bulkBusy ? "Moving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign / Change Assignment Modal */}
      {reassignModalOrder && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10002 }}
          data-testid="reassign-modal-backdrop"
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 440, padding: 24, borderRadius: 10 }}
            data-testid="reassign-modal"
          >
            <div className="modal-head" style={{ marginBottom: 12 }}>
              <div>
                <div className="small-label">ASSIGNMENT MANAGEMENT</div>
                <h2 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>
                  Assign Order #{reassignModalOrder.order_id}
                </h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => !reassignBusy && setReassignModalOrder(null)}
                disabled={reassignBusy}
                data-testid="reassign-modal-close-btn"
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px 0", lineHeight: 1.5 }}>
              Choose an active team member from the {reassignModalOrder.current_stage === "Production" ? "Production" : "Design"} department, or select unassigned to remove the existing assignment.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--navy)",
                  marginBottom: 6,
                }}
              >
                Assigned Specialist:
              </label>
              <select
                value={reassignUser}
                onChange={(e) => setReassignUser(e.target.value)}
                disabled={reassignBusy}
                data-testid="reassign-user-select"
                style={{
                  width: "100%",
                  height: 38,
                  fontSize: 13,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid var(--line)",
                  background: "#fff",
                  color: "var(--navy)",
                }}
              >
                <option value="">-- Unassigned (None) --</option>
                {(reassignModalOrder.current_stage === "Production" ? productionUsers : designUsers).map((u) => (
                  <option key={u.user_id || u.id || u.email} value={u.name}>
                    {u.name} {u.role ? `(${u.role})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setReassignModalOrder(null)}
                disabled={reassignBusy}
                data-testid="reassign-modal-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleSaveReassign}
                disabled={reassignBusy}
                data-testid="reassign-modal-save-btn"
              >
                {reassignBusy ? "Saving…" : "Save Assignment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
